// dependencies
// charting components
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale } from "chart.js";
import React from "react";
import { Bar } from "react-chartjs-2";

// utils
import { getColor } from "../../../../../../../utils/getColor";

// schema
import { Direction } from "../../KPIs/schema";

ChartJS.register(CategoryScale, LinearScale, BarElement);

interface BarchartProps {
	monthlyData: number[];
	direction: Direction;
}

/**
 * Renders a barchart always dipsplaying 12 columns,
 * with data averages per month from the overallSummary data
 * for the time range filter selected, fills in from right to left
 */
const Barchart = (props: BarchartProps) => {
	const { monthlyData, direction } = props;

	// what color should the chart bars be
	const selectChartColor = (direction: Direction) => {
		switch (direction) {
			case "positive":
				return getColor("green", 5);
			case "negative":
				return getColor("red", 5);
			case "zero":
			default:
				return getColor("primary", 5);
		}
	};

	const barColors = () => {
		return monthlyData.map((data) => {
			// keeps us from having a stripe of the color on months with zeros
			return data > 0 ? selectChartColor(direction) : getColor("neutral", 2);
		});
	};

	return (
		<Bar
			options={{
				responsive: true,
				maintainAspectRatio: false,
				elements: {
					bar: {
						borderWidth: 0,
						borderRadius: 2,
					},
				},
				plugins: {
					tooltip: {
						enabled: true,
						position: "nearest",
						xAlign: (context) => {
							const dataIndex = context.tooltip.dataPoints[0]?.dataIndex;
							if (dataIndex === 0) return "left";
							if (dataIndex === 11) return "right";
							return "center";
						},
						yAlign: (context) => {
							const dataIndex = context.tooltip.dataPoints[0]?.dataIndex;
							if (dataIndex === 0) return "center";
							if (dataIndex === 11) return "center";
							return "top";
						},
						displayColors: false,
						padding: 8,
						bodyAlign: "left",
						filter: (tooltipItem) => {
							//filter includes if data index is 1 OR if data index is 0 and dataIndex1 has value 0
							return (
								tooltipItem.datasetIndex === 1 ||
								(tooltipItem.datasetIndex === 0 && monthlyData[tooltipItem.dataIndex] == 0)
							);
						},
						callbacks: {
							label: (context) => {
								// Get the value from the second dataset (Average Score) at the same index
								const value = context.chart.data.datasets[1].data[context.dataIndex] as number;
								const monthIndex = context.dataIndex;
								const currentDate = new Date();
								currentDate.setMonth(currentDate.getMonth() - (11 - monthIndex));
								const monthAbbr = currentDate.toLocaleString("default", { month: "short" });
								return `${monthAbbr} - ${value ? value.toFixed(2) : "None"}`;
							},
							title: () => "",
						},
					},
					legend: {
						display: false,
					},
				},
				hover: { mode: "nearest" },
				scales: {
					x: {
						display: false,
						stacked: true,
					},
					y: {
						max: 5,
						display: false,
					},
				},
			}}
			data={{
				labels: Array(12).fill(""),
				datasets: [
					{
						label: "Bar Backgrounds",
						data: Array(12).fill(5),
						backgroundColor: getColor("neutral", 2),
						barPercentage: 0.83,
						// order means order that the bar is drawn
						order: 1,
					},
					{
						label: "Average Score",
						data: monthlyData,
						backgroundColor: barColors(),
						barPercentage: 0.83,
						// this one is drawn last so ends up on top
						order: 0,
					},
				],
			}}
		/>
	);
};

export default Barchart;
